import { AccountType } from '@constants'
import { useBusinessContext, useDateRangeContext } from '@context'
import {
  dashboardQueryKey,
  decorateExp,
  fetchAccountStatBatch,
  IAccountStatSpec,
  dashboardQueryOptions
} from '@query'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'

export const FIGURE_KEYS = ['revenue', 'profit', 'expenses'] as const

export type FigureKey = (typeof FIGURE_KEYS)[number]

export const FIGURE_ACCOUNT_TYPES: Record<FigureKey, string[]> = {
  revenue: [decorateExp(AccountType.REV)],
  profit: [decorateExp(AccountType.REV), decorateExp(AccountType.EXP)],
  expenses: [decorateExp(AccountType.EXP)]
}

// Equity and purchase payables have their own rows. Everything else on the
// liabilities side (loans, tax, withholding, accruals, VAT, and a future LIA*
// type) shares one row. The batch endpoint only expands positive globs, so
// the remainder is listed from AccountType instead of a negative pattern.
const LIABILITY_TYPES_ON_OWN_ROWS = new Set<AccountType>([
  AccountType.LIA_EQU,
  AccountType.LIA_PRE,
  AccountType.LIA_DUE
])

const otherLiabilityAccountTypes = Object.values(AccountType).filter(
  type => type.startsWith(AccountType.LIA) && !LIABILITY_TYPES_ON_OWN_ROWS.has(type)
)

export const CAPITAL_ACCOUNT_TYPES = {
  shareCapital: [AccountType.LIA_EQU],
  retainedEarnings: [
    AccountType.LIA_PRE,
    decorateExp(AccountType.REV),
    decorateExp(AccountType.EXP)
  ],
  otherLiabilities: otherLiabilityAccountTypes,
  payables: [AccountType.LIA_DUE]
} as const

export type CapitalKey = keyof typeof CAPITAL_ACCOUNT_TYPES

export const useDashboardAccountStats = ({ enabled = true }: { enabled?: boolean } = {}) => {
  const { slug } = useBusinessContext()
  const { selectedRange, isReady, options } = useDateRangeContext()

  const today = dayjs()
  const periodThis = options.find(option => option.key === 'period-this')
  const yearStart = periodThis?.date_from ? dayjs(periodThis.date_from) : null
  const dateAt = selectedRange?.date_to
  const rangeFrom = selectedRange?.date_from
  const rangeTo = selectedRange?.date_to
  const dateFrom = yearStart ? rangeFrom ?? yearStart.format('YYYY-MM-DD') : ''
  const dateTo = rangeTo && dayjs(rangeTo).isBefore(today) ? rangeTo : today.format('YYYY-MM-DD')
  const previousFrom = dateFrom ? dayjs(dateFrom).subtract(1, 'year').format('YYYY-MM-DD') : ''
  const previousTo = dayjs(dateTo).subtract(1, 'year').format('YYYY-MM-DD')

  const capitalSpecs: IAccountStatSpec[] = (
    Object.entries(CAPITAL_ACCOUNT_TYPES) as [CapitalKey, readonly string[]][]
  ).map(([key, accountTypes]) => ({
    key,
    account_type: [...accountTypes],
    date: dateAt
  }))

  const figureSpecs: IAccountStatSpec[] = yearStart
    ? FIGURE_KEYS.flatMap(key => [
        {
          key,
          account_type: FIGURE_ACCOUNT_TYPES[key],
          date_from: dateFrom,
          date_to: dateTo
        },
        {
          key: `${key}__previous`,
          account_type: FIGURE_ACCOUNT_TYPES[key],
          date_from: previousFrom,
          date_to: previousTo
        }
      ])
    : []

  const specs = [...capitalSpecs, ...figureSpecs]
  const queryEnabled =
    enabled && Boolean(slug) && isReady && Boolean(dateAt) && capitalSpecs.length > 0
  const queryKey = dashboardQueryKey(
    slug,
    'accountStats',
    dateAt,
    dateFrom,
    dateTo,
    previousFrom,
    previousTo
  )

  const statsQuery = useQuery({
    queryKey,
    queryFn: () => fetchAccountStatBatch({ slug, specs }),
    ...{ enabled: queryEnabled, ...dashboardQueryOptions(slug) }
  })

  return {
    data: statsQuery.data ?? {},
    isPending: statsQuery.isPending,
    areFiguresPending: yearStart != null && statsQuery.isPending,
    yearStart,
    isReady
  }
}
